// hooks/useWordTranslation.js

import { useState, useCallback, useRef, useEffect } from "react";
import { translateWord, isQuotaExceededError } from "../services/dictionaryApi";
import {
  getTranslationFromDB,
  saveTranslationToDB,
} from "../services/dictionaryStorage";
import { checkUserPremium } from "../utils/handleJWT";

// سقف ترجمه رایگان برای هر دیکشنری
const FREE_LIMIT_PER_DICT = 10;

export const useWordTranslation = () => {
  // state برای شمارش ترجمه‌های رایگان (به تفکیک زبان)
  const [translationCount, setTranslationCount] = useState(() => {
    // تلاش برای بازیابی از sessionStorage (تا وقتی تب بسته نشه)
    try {
      const saved = sessionStorage.getItem("translationCount");
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // state برای تشخیص کاربر ویژه
  const [isPremium, setIsPremium] = useState(false);

  // state برای وضعیت لودینگ
  const [loading, setLoading] = useState(false);

  // ref برای جلوگیری از درخواست‌های همزمان برای یک کلمه
  const pendingRequests = useRef(new Map());

  // بررسی وضعیت ویژه بودن کاربر از توکن
  useEffect(() => {
    const checkPremium = () => {
      const premium = checkUserPremium();
      setIsPremium(premium);
    };

    checkPremium();
    // هر 5 دقیقه یکبار هم چک کن (مثلاً اگر توکن آپدیت شد)
    const interval = setInterval(checkPremium, 5 * 60 * 1000);

    return () => clearInterval(interval);
  }, []);

  // ذخیره شمارش در sessionStorage
  useEffect(() => {
    sessionStorage.setItem(
      "translationCount",
      JSON.stringify(translationCount),
    );
  }, [translationCount]);

  /**
   * بررسی آیا سهمیه رایگان برای یک زبان خاص باقی مانده
   */
  const hasFreeQuota = useCallback(
    (fromLang) => {
      if (isPremium) return true;
      const currentCount = translationCount[fromLang] || 0;
      return currentCount < FREE_LIMIT_PER_DICT;
    },
    [isPremium, translationCount],
  );

  /**
   * افزایش شمارش ترجمه برای یک زبان
   */
  const incrementCount = useCallback((fromLang) => {
    setTranslationCount((prev) => ({
      ...prev,
      [fromLang]: (prev[fromLang] || 0) + 1,
    }));
  }, []);

  /**
   * دریافت ترجمه یک کلمه
   */
  const getTranslation = useCallback(
    async (fromLang, toLang = "fa", word) => {
      if (!word || !fromLang) {
        return {
          error: "PARAMETERS_MISSING",
          message: "کلمه یا زبان مبدا مشخص نشده است",
        };
      }

      // نرمال‌سازی کلمه
      const normalizedWord = word.toLowerCase().trim();

      // ایجاد کلید یکتا برای این درخواست
      const requestKey = `${fromLang}_${toLang}_${normalizedWord}`;

      // اگر همین الان یک درخواست برای این کلمه در حال انجامه، صبر کن تا تموم شه
      if (pendingRequests.current.has(requestKey)) {
        return pendingRequests.current.get(requestKey);
      }

      // ساخت promise برای این درخواست
      const requestPromise = (async () => {
        try {
          setLoading(true);

          // مرحله 1: چک کردن دیتابیس محلی
          const cachedData = await getTranslationFromDB(
            fromLang,
            toLang,
            normalizedWord,
          );

          if (cachedData) {
            return { data: cachedData, fromCache: true };
          }

          // مرحله 2: چک کردن سهمیه رایگان (برای کاربر غیر ویژه)
          if (!isPremium) {
            const currentCount = translationCount[fromLang] || 0;
            if (currentCount >= FREE_LIMIT_PER_DICT) {
              return {
                error: "QUOTA_EXCEEDED",
                message:
                  "سهمیه رایگان روزانه شما برای این دیکشنری تمام شده است",
              };
            }
          }

          // مرحله 3: درخواست از API
          try {
            const response = await translateWord(
              fromLang,
              toLang,
              normalizedWord,
            );

            // اگه درخواست موفق بود
            if (response?.success && response?.data) {
              // 🔥 ساختار جدید: ترکیب data و enData در یک آبجکت
              const combinedData = {
                ...response.data,
                enData: response.enData || [], // اضافه کردن enData به data
              };

              // ذخیره در دیتابیس
              await saveTranslationToDB(
                fromLang,
                toLang,
                normalizedWord,
                combinedData, // ذخیره داده ترکیبی
              );

              // افزایش شمارش (فقط برای کاربر غیر ویژه)
              if (!isPremium) {
                incrementCount(fromLang);
              }

              return { data: combinedData, fromCache: false };
            }

            return {
              error: "UNKNOWN_ERROR",
              message: response?.message || "خطا در دریافت ترجمه",
            };
          } catch (apiError) {
            console.error("❌ خطای API:", apiError);

            // خطای 402 یعنی سهمیه رایگان تموم شده
            if (isQuotaExceededError(apiError)) {
              // اگر کاربر ویژه نیست، سهمیه رو هم کامل نشون بده
              if (!isPremium) {
                // ست کردن کانتر به 10 برای این زبان
                setTranslationCount((prev) => ({
                  ...prev,
                  [fromLang]: FREE_LIMIT_PER_DICT,
                }));
              }

              return {
                error: "QUOTA_EXCEEDED",
                message:
                  "سهمیه رایگان روزانه شما برای این دیکشنری تمام شده است",
                apiError: true,
              };
            }

            // خطاهای دیگه
            const errorMessage =
              apiError?.response?.data?.message ||
              apiError?.message ||
              "خطا در ارتباط با سرور";

            return {
              error: "API_ERROR",
              message: errorMessage,
            };
          }
        } finally {
          setLoading(false);
          // پاک کردن از pending requests
          pendingRequests.current.delete(requestKey);
        }
      })();

      // ذخیره promise در pending requests
      pendingRequests.current.set(requestKey, requestPromise);

      return requestPromise;
    },
    [isPremium, translationCount, incrementCount],
  );

  /**
   * ریست کردن شمارش (مثلاً برای تست)
   */
  const resetCount = useCallback(() => {
    setTranslationCount({});
    sessionStorage.removeItem("translationCount");
  }, []);

  /**
   * دریافت تعداد باقی‌مانده برای یک زبان
   */
  const getRemainingQuota = useCallback(
    (fromLang) => {
      if (isPremium) return Infinity;
      const used = translationCount[fromLang] || 0;
      return Math.max(0, FREE_LIMIT_PER_DICT - used);
    },
    [isPremium, translationCount],
  );

  return {
    getTranslation,
    loading,
    isPremium,
    remainingQuota: getRemainingQuota,
    hasFreeQuota,
    resetCount,
    FREE_LIMIT_PER_DICT,
  };
};
